import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { HttpUtilsService } from '../../commons/http-uilis.service';
import { Observable, catchError, map, mergeMap } from 'rxjs';
import { JsonConvert } from 'json2typescript';
import { Paging } from '../../../model/paging/paging';
import { environment } from '../../../environment/environment';
import { keycloakLogout } from '../../../auth/keycloak.config';
import { AntennaList, AntennaListData, AntennaListDataArray } from '../../../model/antenna/antenna-list';


@Injectable({
    providedIn: 'root'
})
export class AntennaService {
    private _url: string = `${environment.baseAPIURL}/settings`;
    constructor(private _http: HttpClient, private _httpUtils: HttpUtilsService) { }

    GETPaginatedAntennas(
        paging: Paging,
        filters?: { name?: string; model?: string; type?: string; enabled?: boolean | null }
    ): Observable<AntennaList>  | any{
        try {
            return this._httpUtils.getHTTPHeadersAsync().pipe(
                mergeMap(httpHeaders => {
                    return this._http.get(`${this._url}/antennas${this.buildQueryString(paging, filters)}`,{ headers: httpHeaders, withCredentials:true }).pipe(
                        map((res:any) => {
                            const jsonConvert: JsonConvert = new JsonConvert();
                            const response: AntennaListDataArray = jsonConvert.deserializeObject(res, AntennaListDataArray);
                            if (response.success) {
                            	return response;
                            } else {
                            	this._httpUtils.handleError(response);
                                return;
                            }
                        }, catchError(err => {
                            if (err.status === 401) {
                                keycloakLogout({ redirectUri: window.location.origin });
                            }
                            throw err;
                        })));
                }), catchError(err => {
                    throw err;
                })
            );
        } catch (error) {
            throw error;
        }
    }

    GETAntennaDetails(antennaId:string): Observable<AntennaList>  | any{
        try {
            return this._httpUtils.getHTTPHeadersAsync().pipe(
                mergeMap(httpHeaders => {
                    return this._http.get(`${this._url}/antennas/${antennaId}`,{ headers: httpHeaders, withCredentials:true }).pipe(
                        map((res:any) => {
                            const jsonConvert: JsonConvert = new JsonConvert();
                            const response: AntennaListData = jsonConvert.deserializeObject(res, AntennaListData);
                            if (response.success) {
                            	return response;
                            } else {
                            	this._httpUtils.handleError(response);
                                return;
                            }
                        }, catchError(err => {
                            if (err.status === 401) {
                                keycloakLogout({ redirectUri: window.location.origin });
                            }
                            throw err;
                        })));
                }), catchError(err => {
                    throw err;
                })
            );
        } catch (error) {
            throw error;
        }
    }

    PUTAntenna(antennaId:string, body:AntennaList){
        try {
            return this._httpUtils.getHTTPHeadersAsync().pipe(
                mergeMap(httpHeaders => {
                    return this._http.put(`${this._url}/antennas/${antennaId}`, this.toUpdatePayload(body), { headers: httpHeaders, withCredentials:true }).pipe(
                        map((res:any) => {
                            return res;
                        }, catchError(err => {
                            if (err.status === 401) { // Unauthorized
                                keycloakLogout({ redirectUri: window.location.origin });
                            }
                            throw err;
                        })));
                }), catchError(err => {
                    throw err;
                })
            );
        } catch (error) {
            throw error;
        }
    }

    DELETEAntenna(antennaId:string){
        try {
            return this._httpUtils.getHTTPHeadersAsync().pipe(
                mergeMap(httpHeaders => {
                    return this._http.delete(`${this._url}/antennas/${antennaId}`, { headers: httpHeaders, withCredentials:true }).pipe(
                        map((res:any) => {
                            return res;
                        }, catchError(err => {
                            if (err.status === 401) { // Unauthorized
                                keycloakLogout({ redirectUri: window.location.origin });
                            }
                            throw err;
                        })));
                }), catchError(err => {
                    throw err;
                })
            );
        } catch (error) {
            throw error;
        }
    }

    POSTAntenna(body:AntennaList){
        try {
            return this._httpUtils.getHTTPHeadersAsync().pipe(
                mergeMap(httpHeaders => {
                    return this._http.post(`${this._url}/antennas`, this.toCreatePayload(body), { headers: httpHeaders, withCredentials:true }).pipe(
                        map((res:any) => {
                            return res;
                        }, catchError(err => {
                            if (err.status === 401) { // Unauthorized
                                keycloakLogout({ redirectUri: window.location.origin });
                            }
                            throw err;
                        })));
                }), catchError(err => {
                    throw err;
                })
            );
        } catch (error) {
            throw error;
        }
    }

    private buildQueryString(
        paging: Paging,
        filters?: { name?: string; model?: string; type?: string; enabled?: boolean | null }
    ): string {
        const params = new URLSearchParams({
            offset: String((paging.pageIndex - 1) * paging.pageSize),
            limit: String(paging.pageSize)
        });
        if (filters?.name?.trim()) {
            params.set('name', filters.name.trim());
        }
        if (filters?.model) {
            params.set('model', filters.model);
        }
        if (filters?.type) {
            params.set('type', filters.type);
        }
        if (typeof filters?.enabled === 'boolean') {
            params.set('enabled', String(filters.enabled));
        }
        const sort = paging.sort.find((item) => item.value !== null);
        if (sort?.key) {
            params.set('sortBy', sort.key);
        }
        if (sort?.value === 'ascend') {
            params.set('sortOrder', 'asc');
        } else if (sort?.value === 'descend') {
            params.set('sortOrder', 'desc');
        }
        return `?${params.toString()}`;
    }

    private toCreatePayload(body: AntennaList) {
        return {
            name: body.name,
            model: body.model,
            length: body.length,
            type: body.type,
            longitudinalLoss: {
                freq24: body.longitudinalLoss.freq24,
                freq5: body.longitudinalLoss.freq5,
                freq6: body.longitudinalLoss.freq6
            },
            couplingLoss: {
                freq24: body.couplingLoss.freq24,
                freq5: body.couplingLoss.freq5,
                freq6: body.couplingLoss.freq6
            },
            tilt: {
                attenuation: body.tilt.attenuation,
                percentageOfLength: body.tilt.percentageOfLength
            },
            terminationOmni: {
                freq24: {
                    gain: body.terminationOmni.freq24.gain,
                    attenuationFactor: body.terminationOmni.freq24.attenuationFactor
                },
                freq5: {
                    gain: body.terminationOmni.freq5.gain,
                    attenuationFactor: body.terminationOmni.freq5.attenuationFactor
                },
                freq6: {
                    gain: body.terminationOmni.freq6.gain,
                    attenuationFactor: body.terminationOmni.freq6.attenuationFactor
                }
            },
            enabled: body.enabled
        };
    }

    private toUpdatePayload(body: AntennaList) {
        return {
            longitudinalLoss: {
                freq24: body.longitudinalLoss.freq24,
                freq5: body.longitudinalLoss.freq5,
                freq6: body.longitudinalLoss.freq6
            },
            couplingLoss: {
                freq24: body.couplingLoss.freq24,
                freq5: body.couplingLoss.freq5,
                freq6: body.couplingLoss.freq6
            },
            tilt: {
                attenuation: body.tilt.attenuation,
                percentageOfLength: body.tilt.percentageOfLength
            },
            terminationOmni: {
                freq24: {
                    gain: body.terminationOmni.freq24.gain,
                    attenuationFactor: body.terminationOmni.freq24.attenuationFactor
                },
                freq5: {
                    gain: body.terminationOmni.freq5.gain,
                    attenuationFactor: body.terminationOmni.freq5.attenuationFactor
                },
                freq6: {
                    gain: body.terminationOmni.freq6.gain,
                    attenuationFactor: body.terminationOmni.freq6.attenuationFactor
                }
            },
            enabled: body.enabled
        };
    }
}
